/* eslint-disable max-len */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import isEmpty from 'lodash.isempty';

import { getPageOnHostByID, performQuery } from 'lib/api';
import { requireQueryByName } from 'lib/dependencies';
import { encodeFilterData } from 'helpers/filter';
import trackAction from 'utils/tracking';
import { AppContext } from 'context/AppContext';

import QuickNavigation from 'components/QuickNavigation';
import ActivityTheme from 'components/ActivityTheme';
import HeroSlideshow from 'components/Core/HeroSlideshow';
import RichText from 'components/Core/RichText';
import Facility from 'components/Facility';
import Faq from 'components/Faq';
import PopularActivity from 'components/PopularActivity';
import CtaBlock from 'components/CtaBlock';
import Newsletter from 'components/Newsletter';
import LatestNews from 'components/LatestNews';
import Contact from 'components/Contact';
import Embed from 'components/Core/Embed';

const HomePage = (props) => {
  const { updateAppState } = useContext(AppContext);
  const {
    hostname,
    page: {
      id: pageID,
      carousel,
      body,
      activityChooserTitle,
      activityChooserSubtitle,
      activityChooserThemes,
      activityChooserIndex,
      newsletterTitle,
      newsletterBody,
      recaptchaKey,
    },
    showWizard: showWizardSetting,
    siteName,
    wizardQuestions,
    wizardAssistant,
  } = props;

  const router = useRouter();
  const toFilterIndexPage = async (filterData, themeID, cb) => {
    const { url } = activityChooserIndex;
    let pathAs = `${url}?theme=${themeID}`;
    let queryParams = {
      theme: themeID,
    };
    if (!isEmpty(filterData)) {
      pathAs = `${pathAs}&filters=${filterData}`;
      queryParams = { ...queryParams, filters: `${filterData}` };
    }
    router
      .replace(
        {
          pathname: `/[...slug]?theme=${themeID}&filters=${filterData}`,
          query: queryParams,
        },
        pathAs,
      )
      .then(() => {
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
        cb();
      });
  };

  const onQNavItemClick = (itemLabel) => {
    trackAction('quick-navigation', itemLabel, 'click');
  };

  const onFilterClick = (filtersList, themeId, cb) => {
    const filterData = isEmpty(filtersList) ? null : encodeFilterData(filtersList);
    toFilterIndexPage(filterData, themeId, cb);
  };

  const wizardClose = () => {
    document.body.classList.remove('modal-open');
    updateAppState((state) => ({
      ...state,
      showWizard: false,
    }));
  };

  const wizardOpen = async (selectedTheme) => {
    // Set the wizard loading state
    document.body.classList.add('modal-open');
    updateAppState((state) => ({
      ...state,
      wizardProps: {
        isLoading: true,
      },
      showWizard: true,
    }));
    // Get the activity count
    const { themeID } = selectedTheme;
    const updatedResultCountResp = await performQuery(
      `query($homePageID: Int!, $filters:[FilterType], $themeId: Int){
              activitiesResultCount(filters:$filters, homePageId: $homePageID, themeId: $themeId)
          }`,
      {
        homePageID: parseInt(pageID, 10),
        filters: [],
        themeId: themeID,
      },
    );
    const { activitiesResultCount } = updatedResultCountResp;
    // Update the wizard data
    updateAppState((state) => ({
      ...state,
      wizardProps: {
        currentPageId: parseInt(pageID, 10),
        wizardQuestions,
        wizardAssistant,
        selectedTheme,
        initialResultCount: activitiesResultCount,
        onClickCloseCB: wizardClose,
        conClickFilterCB: onFilterClick,
        overviewPage: activityChooserIndex,
        isLoading: false,
      },
    }));
  };

  const activityThemeData = {
    siteName,
    title: activityChooserTitle,
    subtitle: activityChooserSubtitle,
    themes: activityChooserThemes,
    onClickTheme: ({ name: themeName, id }) => {
      wizardOpen({ themeName, themeID: parseInt(id, 10) });
    },
    overviewPage: activityChooserIndex,
  };
  const showNewsLetter = newsletterTitle && newsletterBody;
  return (
    <>
      {carousel.length > 0 && (
        <HeroSlideshow extraClassNames={!showWizardSetting ? 'mb-6 lg:mb-12' : ''} carousel={carousel} />
      )}
      {activityThemeData && (
        <div className="relative -mt-10 lg:-mt-20">
          <ActivityTheme {...activityThemeData} showWizardSetting={showWizardSetting} />
        </div>
      )}
      {(body || []).map((block) => {
        if (block.blockType === 'QuickNavigationBlock') {
          return <QuickNavigation {...block} onItemClickCB={onQNavItemClick} key={`${block.blockType}-${block.id}`} />;
        }
        if (block.blockType === 'FAQBlock') {
          return <Faq key={`block-faq-${block.id}`} {...block} />;
        }
        if (block.blockType === 'FacilityBlock') {
          return <Facility {...block} key={`${block.blockType}-${block.id}`} />;
        }
        if (block.blockType === 'PopularActivityBlock') {
          return <PopularActivity {...block} key={`${block.blockType}-${block.id}`} />;
        }
        if (block.blockType === 'CTABlock') {
          return <CtaBlock {...block} key={`${block.blockType}-${block.id}`} />;
        }
        if (block.blockType === 'LatestNewsBlock') {
          return <LatestNews {...block} key={`${block.blockType}-${block.id}`} />;
        }
        if (block.blockType === 'ContactBlock') {
          return <Contact hostname={hostname} {...block} key={`${block.blockType}-${block.id}`} />;
        }
        if (block.blockType === 'RichTextBlock') {
          return (
            <div key={`${block.blockType}-${block.id}`} className="container mx-auto px-4">
              <div className="flex flex-wrap">
                <div className="flex-full max-w-full lg:flex-2/3 lg:max-w-2/3">
                  <RichText content={block.value} />
                </div>
              </div>
            </div>
          );
        }
        if (block.blockType === 'CustomEmbedBlock') {
          return (
            <div key={`${block.blockType}-${block.id}`} className="container mx-auto px-4">
              <div className="u-block-margins">
                <Embed title={block.title} {...block.embed} />
              </div>
            </div>
          );
        }
        return <div key={`${block.blockType}-${block.id}`} />;
      })}
      {showNewsLetter && (
        <Newsletter
          hostname={hostname}
          newsletterTitle={newsletterTitle}
          newsletterBody={newsletterBody}
          recaptchaKey={recaptchaKey}
        />
      )}
    </>
  );
};

// The data returned here will be send as `props` to the component
// TODO: Let's see if we need this here or can ultimately adopt this into the lib/api calls directly
// That will depend on how different these calls are going to be along the way.
HomePage.getCustomInitialProps = async ({ id, hostname, contentType, previewData }) => {
  const query = requireQueryByName('HomePage');
  const token = previewData ? previewData.token : '';
  const { page, wizardQuestions, wizardAssistant } = await getPageOnHostByID({
    id,
    contentType,
    query,
    token,
    hostname,
  });

  return {
    page,
    extraPageProps: {
      hostname,
      wizardQuestions,
      wizardAssistant,
    },
  };
};

HomePage.defaultProps = {
  page: {
    activityChooserSubtitle: null,
    newsletterTitle: null,
    newsletterBody: null,
  },
  wizardQuestions: [],
  wizardAssistant: null,
  siteName: '',
};

HomePage.propTypes = {
  page: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    body: PropTypes.arrayOf(PropTypes.shape({})),
    carousel: PropTypes.arrayOf(PropTypes.shape({})),
    newsletterTitle: PropTypes.string,
    newsletterBody: PropTypes.string,
    recaptchaKey: PropTypes.string.isRequired,
    activityChooserTitle: PropTypes.string.isRequired,
    activityChooserSubtitle: PropTypes.string,
    activityChooserThemes: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        icon: PropTypes.string,
        id: PropTypes.string,
      }),
    ),
    activityChooserIndex: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  showWizard: PropTypes.bool.isRequired,
  wizardQuestions: PropTypes.arrayOf(
    PropTypes.shape({
      theme: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }).isRequired,
      content: PropTypes.string,
      filterSet: PropTypes.shape({
        slug: PropTypes.string,
        id: PropTypes.string,
        filters: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            slug: PropTypes.string,
          }),
        ),
      }),
    }),
  ),
  wizardAssistant: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    helpText: PropTypes.string,
    avatar: PropTypes.shape({
      image: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
    content: PropTypes.string,
  }),
  siteName: PropTypes.string,
  hostname: PropTypes.string.isRequired,
};

export default HomePage;
